import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { TextFieldContext } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { Button, IconButton, ListBox, ListBoxItem, Popover, Select, SelectValue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type {
  AutocompleteAction,
  FieldOptions,
  FilterToken,
  FlattenedToken,
  ParsedToken,
  RelationalFieldOption,
  RenderAutocompleteDetails,
} from '../../types/QueryBuilder';

import { useQueryBuilderContext } from './context';
import { InlineFilterMenu } from './fields/InlineFilterMenu';
import { FilterBarContext, useFilterBarContext } from './FilterBarContext';
import type { FieldSampleValue, FieldSearchHandler, FilterBarContextValue, LoadFieldValues } from './FilterBarContext';
import { useApplyFocusOverride } from './hooks/useApplyFocusOverride';
import { useGridSelection } from './hooks/useGridSelection';
import { KeyboardSelectionContext, useKeyboardSelection } from './hooks/useKeyboardSelection';
import { useUndoStack } from './hooks/useUndoStack';
import { SelectionKeyHandler } from './SelectionKeyHandler';
import { FreeTextToken } from './tokens/FreeTextToken';
import { LogicBoolToken } from './tokens/LogicBoolToken';
import { LogicNotToken } from './tokens/LogicNotToken';
import { LogicParen } from './tokens/LogicParen';
import { LozengeFilter } from './tokens/LozengeFilter';
import { flattenTokens, tokenValueTexts } from './utils';

import styles from './FilterBar.module.css';

export { useFilterBarContext };
export type { FieldSampleValue, FieldSearchHandler, FilterBarContextValue, LoadFieldValues };

export type PinnedOption = {
  value: string;
  label: string;
};

// React Aria Select won't clear on re-select; this ListBox id is the only way back to unpinned.
const CLEAR_PINNED_KEY = '__filterbar_clear_pinned__';

type FilterBarProps = {
  /** When true, the bar is rendered read-only: token cells are disabled, popovers don't
   * open, remove buttons are hidden, and the cursor-landing FreeText inputs are skipped.
   * Defaults to false (editable). */
  readonly?: boolean;
  preserveEditableLayout?: boolean;
  /** Field options used both for FreeTextToken key autocomplete and per-token LozengeFilter value autocomplete. */
  fieldOptions?: FieldOptions[];
  showSearchIcon?: boolean;
  /** When provided, applied as the `id` of the trailing FreeTextToken input for label association. */
  inputId?: string;
  /**
   * Id of a visible label element used to name the bar's `role="textbox"` container via
   * `aria-labelledby`. Preferred over a `<label htmlFor>` pointing at the inner input, since the
   * focusable element is the container div (not a labelable form element). Replaces the default
   * "Search query" aria-label when set.
   */
  ariaLabelledby?: string;
  /**
   * When false, suppresses the inline type chip (`string` / `number` / `boolean`)
   * next to field-key autocomplete options. Defaults to true. Useful for field
   * sets where the underlying value type isn't meaningful to the user.
   */
  showTypeLabels?: boolean;
  /**
   * When true (default), the operator picker offers "exists" / "does not exist".
   * Set false where the bar's consumer has no way to store that choice when the query is saved.
   */
  allowExistsOption?: boolean;
  /**
   * When true, autocomplete and direct typing can insert a standalone `NOT`
   * token before a filter or expression. Keep this off for consumers whose
   * payload API cannot represent unary negation.
   */
  allowNotOperator?: boolean;
  /**
   * Optional list of typed-group prefixes (e.g. anySpan / noSpan). When provided
   * (and non-empty) the FreeText autocomplete surfaces a "Relational Fields"
   * section, and the LogicParen type cell offers them in its picker. When
   * omitted, no Relational Fields UI is shown.
   */
  relationalFieldOptions?: RelationalFieldOption[];
  /** Placeholder for the trailing FreeText input while the bar is empty; omitted = none. */
  placeholder?: string;
  /**
   * Renders the bar with the standard Launchpad invalid border. When omitted, the
   * bar reads `isInvalid` from a surrounding `TextFieldContext` (e.g. set by a
   * Launchpad `FieldGroup` with `errorMessage`) so wrapping it in a FieldGroup
   * gives the same red-outline behavior as a regular input.
   */
  isInvalid?: boolean;
  /** Fields rendered by their own control, so the bar shows no lozenge and no menu entry for them. */
  omitFields?: string[];
  variant?: 'default' | 'inline';
  pinnedFields?: string[];
  /** Inline variant: applies multi-value selections immediately without closing the filter menu. */
  menuMultiSelect?: boolean;
  /** Inline variant: shows a visible action that clears the complete query. */
  clearAllLabel?: string;
  /** Inline variant: render the Filter trigger visible but disabled. */
  menuDisabled?: boolean;
  /** Inline variant: when false, a single selected value keeps its complete display label. */
  truncateInlineSingleValue?: boolean;
  /** Applied to the bar's outer element so consumers can override its styling (e.g. a compact inline variant). */
  className?: string;
  autocompleteFooter?: React.ReactNode;
  autocompleteHeader?: React.ReactNode;
  autocompleteActions?: AutocompleteAction[];
  /**
   * Renders a metadata panel beside key, logical-operator/grouping, comparison-operator,
   * relational-field, and value suggestions. Return `null` for options the consumer has nothing
   * to say about; descriptions for options with panel content are omitted from the list.
   */
  autocompleteDetails?: RenderAutocompleteDetails;
  pinnedOptions?: PinnedOption[];
  pinnedValue?: string;
  onPinnedChange?: (value: string | undefined) => void;
  pinnedPlaceholder?: string;
  pinnedAriaLabel?: string;
  onLoadValues?: LoadFieldValues;
  /** Called as the user types in a field-key autocomplete input. */
  onFieldSearch?: FieldSearchHandler;
};

/**
 * Returns the character offset just after the end of a flattened token in the query string.
 * Used to compute the insertion point for auto-inserted FreeTextTokens.
 */
function getFlatTokenQueryEnd(token: FlattenedToken): number {
  if (token.type === 'PAREN') {
    if (token.side === 'open') {
      // Insert just inside the opening parenthesis. The expression's start offset
      // points at the FIRST character of the prefix (when present, e.g. `anySpan(`),
      // so we need to skip the prefix's length AND the literal `(` to land between
      // the parens — otherwise content gets spliced in mid-prefix.
      const prefixLen = token.expression.expressionType?.length ?? 0;
      return token.expression.location.start.offset + prefixLen + 1;
    }
    // close paren: insert just after the closing parenthesis
    return token.expression.location.end.offset;
  }
  return token.location.end.offset;
}

function isFieldOmitted(token: FlattenedToken, omitFields?: string[]): boolean {
  return token.type === 'FILTER' && !!omitFields?.includes(token.field);
}

/** Omit hidden FILTERs; keep a LOGIC_BOOL only when it is the first connector between two surviving content tokens. */
function omittedTokenIndices(flatTokens: FlattenedToken[], omitFields?: string[]): Set<number> {
  const omitted = new Set<number>();
  if (!omitFields?.length) {
    return omitted;
  }

  for (let i = 0; i < flatTokens.length; i++) {
    if (isFieldOmitted(flatTokens[i], omitFields)) {
      omitted.add(i);
      // Unary NOT belongs to the expression immediately after it. Hiding that
      // expression must not leave a dangling negation pill behind.
      for (let j = i - 1; j >= 0 && flatTokens[j].type === 'LOGIC_NOT'; j--) {
        omitted.add(j);
      }
    }
  }

  const isContent = (index: number) =>
    !omitted.has(index) && flatTokens[index].type !== 'LOGIC_BOOL' && flatTokens[index].type !== 'LOGIC_NOT';

  for (let i = 0; i < flatTokens.length; i++) {
    if (flatTokens[i].type !== 'LOGIC_BOOL') {
      continue;
    }

    let left = -1;
    for (let j = i - 1; j >= 0; j--) {
      if (isContent(j)) {
        left = j;
        break;
      }
    }
    let right = -1;
    for (let j = i + 1; j < flatTokens.length; j++) {
      if (isContent(j)) {
        right = j;
        break;
      }
    }
    if (left < 0 || right < 0) {
      omitted.add(i);
      continue;
    }

    let earlierConnector = false;
    for (let j = left + 1; j < i; j++) {
      if (flatTokens[j].type === 'LOGIC_BOOL') {
        earlierConnector = true;
        break;
      }
    }
    if (earlierConnector) {
      omitted.add(i);
    }
  }

  return omitted;
}

/** Grid keys for visible tokens only; keep original indices so focus-override targets stay valid. */
function buildAllKeys(flatTokens: FlattenedToken[], omitted: Set<number>): string[] {
  const keys: string[] = [];
  if (flatTokens.length === 0) {
    keys.push('ft-start');
    return keys;
  }
  if (flatTokens[0].type !== 'FREE_TEXT') {
    keys.push('ft-start');
  }
  flatTokens.forEach((token, i) => {
    if (omitted.has(i)) {
      return;
    }
    keys.push(String(i));
    const next = flatTokens[i + 1];
    if (token.type !== 'FREE_TEXT' && (next === undefined || next.type !== 'FREE_TEXT')) {
      keys.push(`ft-after-${i}`);
    }
  });
  return keys;
}

/** Returns true for keys that represent FREE_TEXT cursor-landing tokens. */
function makeIsFreeTextKey(flatTokens: FlattenedToken[]): (key: string) => boolean {
  return (key: string) => {
    if (key.startsWith('ft-')) {
      return true;
    }
    const i = parseInt(key, 10);
    if (isNaN(i)) {
      return false;
    }
    return flatTokens[i]?.type === 'FREE_TEXT';
  };
}

type FilterParsedToken = Extract<ParsedToken, { type: 'FILTER' }>;

/**
 * Convert a parsed FILTER token into the FilterToken shape consumed by LozengeFilter.
 * Multi-value tokens keep their array shape so individual values (including any that
 * contain commas) survive the round-trip instead of collapsing into one string.
 */
function toFilterToken(token: FilterParsedToken): FilterToken {
  const texts = tokenValueTexts(token);
  return { field: token.field, operator: token.operator, value: texts.length === 1 ? texts[0] : texts };
}

function DefaultFilterBar({
  readonly = false,
  preserveEditableLayout = false,
  fieldOptions,
  showSearchIcon = false,
  inputId,
  showTypeLabels = true,
  allowExistsOption = true,
  allowNotOperator = false,
  relationalFieldOptions,
  isInvalid,
  omitFields,
  className,
  placeholder,
  autocompleteFooter,
  autocompleteHeader,
  autocompleteActions,
  autocompleteDetails,
  ariaLabelledby,
  pinnedOptions,
  pinnedValue,
  onPinnedChange,
  pinnedPlaceholder = 'Pinned',
  pinnedAriaLabel,
  onLoadValues,
  onFieldSearch,
}: FilterBarProps) {
  const { dispatch, focusOverride, wrapperRef, query, committedQuery, parsedQuery, activePopover, setActivePopover } =
    useQueryBuilderContext();
  const textFieldContext = useContext(TextFieldContext);
  const contextIsInvalid =
    textFieldContext && 'isInvalid' in textFieldContext ? Boolean(textFieldContext.isInvalid) : false;
  const invalid = isInvalid ?? contextIsInvalid;

  const flatTokens = flattenTokens(parsedQuery);

  const omittedTokens = omittedTokenIndices(flatTokens, omitFields);
  const allKeys = buildAllKeys(flatTokens, omittedTokens);
  const isFreeTextKey = makeIsFreeTextKey(flatTokens);

  const freeTextFieldOptions =
    omitFields && omitFields.length > 0 && fieldOptions
      ? fieldOptions.filter((o) => !omitFields.includes(o.field))
      : fieldOptions;

  const hasAnyVisibleToken = flatTokens.some((_, i) => !omittedTokens.has(i));

  const outerRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  // role="grid" requires role="row" descendants. The grid attributes are applied below
  // only when there's at least one rendered token row — see the gridProps narrowing
  // after `renderedTokens` is built.

  const gridSelection = useGridSelection();
  const gridSelectionRef = useRef(gridSelection);
  useLayoutEffect(() => {
    gridSelectionRef.current = gridSelection;
  });

  // When the selection becomes non-empty, move DOM focus to the hidden input and
  // clear the virtual focused key so no individual token row appears active.
  // Depend on the size primitive (not the Set reference) so unrelated dispatches
  // that produce a new selectedKeys Set with the same contents don't re-steal focus.
  const selectionSize = gridSelection.selectedKeys.size;
  useEffect(() => {
    if (selectionSize > 0) {
      gridSelectionRef.current.setFocusedKey(null);
      hiddenInputRef.current?.focus();
    }
  }, [selectionSize]);

  useApplyFocusOverride(focusOverride, dispatch, gridSelection, wrapperRef, allKeys, setActivePopover);

  // After a commit, move focus to the trailing FreeText input so the user can chain
  // another filter without re-clicking. Skip when:
  //  - focusOverride is set: the commit already targets focus (e.g. inside a new group).
  //  - a lozenge cell popover is open: the user is still editing that cell. Selecting a
  //    value moves DOM focus into the popover, which is portaled outside the bar, so the
  //    bar's onBlur commits — stealing focus here would blur the cell and dismiss the
  //    popover, ending a multiselect after a single checkbox click.
  //  - focus already left the bar (onBlur, or a removed lozenge dropped focus to body).
  //  - a selection is active: the hidden selection input owns focus.
  //  - readonly: no editable trailing input exists.
  const prevCommittedRef = useRef(committedQuery);
  useLayoutEffect(() => {
    const prevCommitted = prevCommittedRef.current;
    prevCommittedRef.current = committedQuery;
    if (readonly || committedQuery === prevCommitted || focusOverride !== null || activePopover !== null) {
      return;
    }
    const outer = outerRef.current;
    if (!outer || !outer.contains(document.activeElement)) {
      return;
    }
    if (gridSelectionRef.current.selectedKeys.size > 0) {
      return;
    }
    const trailingKey = allKeys.at(-1);
    if (trailingKey === undefined) {
      return;
    }
    outer.querySelector<HTMLInputElement>(`input[data-key="${CSS.escape(trailingKey)}"]`)?.focus();
  }, [committedQuery, focusOverride, readonly, allKeys, activePopover]);

  const { undo, redo } = useUndoStack({ query: query.trim(), focusOverride, state: gridSelection, dispatch });

  const keyboardSelCtxValue = useKeyboardSelection({
    state: gridSelection,
    allKeys,
    isFreeTextKey,
    flatTokens,
    hiddenInputRef,
  });

  // Stable ref so the drag-selection effect can always read the latest context.
  const selCtxRef = useLatestRef(keyboardSelCtxValue);

  // Set by the drag-selection mouseup handler when the just-completed gesture was a real drag
  // (not a click). The click event that follows mouseup checks this and bails so it doesn't
  // refocus an input — refocusing would blur the SelectionKeyHandler hidden input, whose
  // onBlur clears the selection that the drag just produced.
  const dragJustCompletedRef = useRef(false);

  function handleClick(e: React.MouseEvent) {
    // Don't hijack clicks on interactive elements — let them handle their own focus.
    if ((e.target as Element).closest('input, button, [role="button"], [role="combobox"]')) {
      return;
    }
    // Suppress the click that fires immediately after a drag-selection mouseup.
    if (dragJustCompletedRef.current) {
      dragJustCompletedRef.current = false;
      return;
    }
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    // Recover flex-wrap visual rows by grouping direct children by offsetTop —
    // siblings that wrap to the same row share the same value.
    const rowMap = new Map<number, HTMLElement[]>();
    for (const child of Array.from(grid.children) as HTMLElement[]) {
      const list = rowMap.get(child.offsetTop);
      if (list) {
        list.push(child);
      } else {
        rowMap.set(child.offsetTop, [child]);
      }
    }
    const rows = [...rowMap.values()];
    if (rows.length === 0) {
      return;
    }

    // Hit-test the click Y against each row. The last row's lower bound is the grid's
    // bottom so clicks in trailing whitespace at the end of a row still register.
    const gridBottom = grid.getBoundingClientRect().bottom;
    let targetRow: HTMLElement[] | null = null;
    for (let i = 0; i < rows.length; i++) {
      const rowTop = rows[i][0].getBoundingClientRect().top;
      const rowBottom = i + 1 < rows.length ? rows[i + 1][0].getBoundingClientRect().top : gridBottom;
      if (e.clientY >= rowTop && e.clientY < rowBottom) {
        targetRow = rows[i];
        break;
      }
    }

    // Click landed outside any row (e.g. in outer padding around the grid) — fall back
    // to the last row so the filter bar still feels clickable.
    const focusRow = targetRow ?? rows[rows.length - 1];
    const inputs = focusRow.flatMap((_el) => Array.from(_el.querySelectorAll<HTMLInputElement>('input')));
    if (inputs.length === 0) {
      return;
    }

    for (let i = 0; i < focusRow.length; i++) {
      const child = focusRow[i];
      const spacer = child.querySelector<HTMLInputElement>(
        'input[data-freetext-token]:not([data-trailing]):not([data-has-value])',
      );
      if (!spacer) {
        continue;
      }
      const prev = focusRow[i - 1];
      const next = focusRow[i + 1];
      if (!prev || !next) {
        continue;
      }
      const prevIsContent = Boolean(
        prev.querySelector('[data-token-container], [data-freetext-token][data-has-value]'),
      );
      const nextIsContent = Boolean(
        next.querySelector('[data-token-container], [data-freetext-token][data-has-value]'),
      );
      if (!prevIsContent || !nextIsContent) {
        continue;
      }
      const prevRect = prev.getBoundingClientRect();
      const nextRect = next.getBoundingClientRect();
      if (
        e.clientX >= prevRect.right &&
        e.clientX <= nextRect.left &&
        e.clientY >= Math.min(prevRect.top, nextRect.top) &&
        e.clientY <= Math.max(prevRect.bottom, nextRect.bottom)
      ) {
        spacer.focus();
        return;
      }
    }

    // Of the inputs in that row, focus the one whose horizontal center is closest to
    // the click X — this makes a click between two tokens land on the nearer input.
    let closest = inputs[0];
    let closestDistance = Infinity;
    for (const input of inputs) {
      const rect = input.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const distance = Math.abs(center - e.clientX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = input;
      }
    }
    closest.focus();
  }

  // Drag selection — native DOM listeners so we aren't limited by React's synthetic
  // event system. Skipped entirely in readonly mode: a readonly bar shouldn't let
  // users select lozenges as a unit (only their inner text remains selectable via
  // normal browser text selection).
  useEffect(() => {
    if (readonly) {
      return;
    }
    const elOrNull = gridRef.current;
    if (!elOrNull) {
      return;
    }
    // Bind to a non-null alias so inner function closures below keep the narrowed type.
    const el: HTMLDivElement = elOrNull;

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let animFrame: number | null = null;
    let rectCache: Array<{ key: string; rect: DOMRect }> | null = null;

    function handleMouseDown(e: MouseEvent) {
      // Gate: a mousedown inside a content-token row should not start drag selection.
      if ((e.target as Element).closest('[data-token-container]')) {
        return;
      }
      isDown = true;
      startX = e.clientX;
      startY = e.clientY;
      dragging = false;
    }

    function handleMouseMove(e: MouseEvent) {
      if (!isDown) {
        return;
      }
      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);

      if (!dragging) {
        if (dx > 10 || dy > 10) {
          dragging = true;
          // Cache the bounding rect of every keyed token element for the drag duration.
          rectCache = Array.from(el.querySelectorAll<HTMLElement>('[data-key]')).flatMap((tokenEl) => {
            const { key } = tokenEl.dataset;
            if (key === undefined) {
              return [];
            }
            return [{ key, rect: tokenEl.getBoundingClientRect() }];
          });
          // Suppress hover/click effects on content tokens during the drag.
          el.querySelectorAll<HTMLElement>('[data-token-container]').forEach((tokenEl) => {
            const { style } = tokenEl;
            style.pointerEvents = 'none';
          });
        } else {
          return;
        }
      }

      if (animFrame !== null) {
        return;
      }
      const currentX = e.clientX;
      const currentY = e.clientY;
      animFrame = requestAnimationFrame(() => {
        animFrame = null;
        if (!rectCache) {
          return;
        }
        const minX = Math.min(startX, currentX);
        const maxX = Math.max(startX, currentX);
        const minY = Math.min(startY, currentY);
        const maxY = Math.max(startY, currentY);
        const selected = new Set<string>();
        for (const { key, rect } of rectCache) {
          // Select every token whose row overlaps the dragged rectangle.
          if (rect.right >= minX && rect.left <= maxX && rect.bottom >= minY && rect.top <= maxY) {
            selected.add(key);
          }
        }
        // Fill in any FREE_TEXT boundary tokens between the leftmost and rightmost
        // selected keys so the selection appears continuous.  FreeText inputs are
        // narrow and are easily missed by the drag rect, but conceptually anything
        // spanning the selected range should be included.
        if (selected.size > 0) {
          const keys = selCtxRef.current.allKeys;
          const indices = Array.from(selected)
            .map((k) => keys.indexOf(k))
            .filter((i) => i !== -1);
          if (indices.length > 0) {
            const lo = Math.min(...indices);
            const hi = Math.max(...indices);
            for (let i = lo; i <= hi; i++) {
              selected.add(keys[i]);
            }
          }
        }
        selCtxRef.current.state.setSelectedKeys(selected);
      });
    }

    function handleMouseUp() {
      if (dragging) {
        // Re-enable pointer events and discard the rect cache.
        el.querySelectorAll<HTMLElement>('[data-token-container]').forEach((tokenEl) => {
          const { style } = tokenEl;
          style.pointerEvents = '';
        });
        // Tell handleClick (which fires next on the wrapper) to skip its refocus. A drag
        // produces a selection; refocusing an input here would blur the hidden selection
        // handler and clear the selection.
        dragJustCompletedRef.current = true;
      }
      isDown = false;
      dragging = false;
      rectCache = null;
      if (animFrame !== null) {
        cancelAnimationFrame(animFrame);
        animFrame = null;
      }
    }

    el.addEventListener('mousedown', handleMouseDown);
    el.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (animFrame !== null) {
        cancelAnimationFrame(animFrame);
      }
      el.removeEventListener('mousedown', handleMouseDown);
      el.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- stable via refs; readonly intentionally re-binds the effect
  }, [readonly]);

  // Auto-render the flattened token list. FreeTextTokens are interleaved between
  // content tokens only when editable — readonly bars wouldn't accept typed input
  // anyway, and rendering them as live <input>s defeats the read-only intent.
  const renderedTokens: React.ReactNode[] = [];
  if (!readonly && (flatTokens.length === 0 || flatTokens[0].type !== 'FREE_TEXT')) {
    renderedTokens.push(
      <FreeTextToken
        key="ft-start"
        tokenKey="ft-start"
        options={freeTextFieldOptions}
        queryInsertOffset={0}
        isTrailing={!hasAnyVisibleToken}
        // Only the empty bar shows the placeholder — once a token exists, a
        // leading FreeText cell is just a cursor-landing gap and shouldn't read
        // like an empty input.
        {...(flatTokens.length === 0 ? { placeholder } : {})}
        allowNotOperator={allowNotOperator}
      />,
    );
  }
  flatTokens.forEach((token, i) => {
    const tokenKey = String(i);
    if (omittedTokens.has(i)) {
      return;
    }
    if (token.type === 'FILTER') {
      renderedTokens.push(
        <LozengeFilter
          key={tokenKey}
          tokenKey={tokenKey}
          value={toFilterToken(token)}
          onChange={() => {}}
          onRemove={() => dispatch({ type: 'DELETE_TOKEN', token })}
          removable={!readonly || preserveEditableLayout}
          options={fieldOptions}
          parsedToken={token}
        />,
      );
    } else if (token.type === 'LOGIC_BOOL') {
      renderedTokens.push(
        <LogicBoolToken
          key={tokenKey}
          tokenKey={tokenKey}
          token={token}
          onChange={() => {}}
          onRemove={() => dispatch({ type: 'DELETE_TOKEN', token })}
          removable={!readonly || preserveEditableLayout}
        />,
      );
    } else if (token.type === 'LOGIC_NOT') {
      renderedTokens.push(
        <LogicNotToken
          key={tokenKey}
          tokenKey={tokenKey}
          token={token}
          onRemove={() => dispatch({ type: 'DELETE_TOKEN', token })}
          removable={!readonly || preserveEditableLayout}
        />,
      );
    } else if (token.type === 'PAREN') {
      renderedTokens.push(
        <LogicParen
          key={tokenKey}
          tokenKey={tokenKey}
          token={token}
          onRemove={() => dispatch({ type: 'UNWRAP_EXPRESSION', token: token.expression })}
          removable={(!readonly || preserveEditableLayout) && token.side === 'close'}
          expressionType={token.expression.expressionType}
        />,
      );
    } else if (token.type === 'FREE_TEXT') {
      renderedTokens.push(
        <FreeTextToken
          key={tokenKey}
          tokenKey={tokenKey}
          initialValue={token.value}
          options={freeTextFieldOptions}
          parsedToken={token}
          allowNotOperator={allowNotOperator}
        />,
      );
    }

    if (!readonly && token.type !== 'FREE_TEXT') {
      const next = flatTokens[i + 1];
      if (next === undefined || next.type !== 'FREE_TEXT') {
        const hasVisibleAfter = flatTokens.some((_, j) => j > i && !omittedTokens.has(j));
        renderedTokens.push(
          <FreeTextToken
            key={`ft-after-${i}`}
            tokenKey={`ft-after-${i}`}
            options={freeTextFieldOptions}
            queryInsertOffset={getFlatTokenQueryEnd(token)}
            isTrailing={!hasVisibleAfter}
            allowNotOperator={allowNotOperator}
          />,
        );
      }
    }
  });

  const gridProps: React.HTMLAttributes<HTMLDivElement> =
    renderedTokens.length > 0 ? { role: 'grid', 'aria-label': msg('queryBuilder.filterBar.gridLabel') } : {};

  return (
    <FilterBarContext.Provider
      value={{
        readonly,
        // Readonly bars opt out of grid selection — descendants that read this
        // (via useQueryBuilderGridItem) skip the data-key / aria-selected wiring,
        // so lozenges and parens are no longer addressable as selectable rows.
        gridState: readonly ? null : gridSelection,
        inputId,
        showTypeLabels,
        allowExistsOption,
        relationalFieldOptions,
        omitFields,
        placeholder,
        autocompleteFooter,
        autocompleteHeader,
        autocompleteActions,
        autocompleteDetails,
        onLoadValues,
        onFieldSearch,
      }}
    >
      <KeyboardSelectionContext.Provider value={keyboardSelCtxValue}>
        <div
          role="textbox"
          aria-label={ariaLabelledby ? undefined : msg('queryBuilder.filterBar.searchQueryLabel')}
          aria-labelledby={ariaLabelledby}
          tabIndex={0}
          aria-readonly={readonly || undefined}
          onClick={handleClick}
          onKeyDown={(e) => {
            // Undo (Cmd/Ctrl+Z) and redo (Cmd/Ctrl+Shift+Z, Ctrl+Y), handled on the outer
            // container — not the grid — so they fire whether focus is in a grid input or on
            // the hidden SelectionKeyHandler input (a sibling of the grid, whose events never
            // reach grid-level handlers). Bubble phase (not capture) so a focused free-text
            // input with uncommitted text can stopPropagation first and keep native input
            // undo/redo while it's dirty (see FreeTextToken).
            // Skip on readonly bars: they're display-only (e.g. the AI-suggestion preview
            // driven by a controlled `value` with no onChange), so a query-history undo/redo
            // would revert the internal state and desync the preview from its `value` prop.
            const isZ = e.key === 'z' || e.key === 'Z';
            const isRedo =
              !readonly && (e.ctrlKey || e.metaKey) && ((isZ && e.shiftKey) || e.key === 'y' || e.key === 'Y');
            const isUndo = !readonly && (e.ctrlKey || e.metaKey) && isZ && !e.shiftKey;
            if (isRedo || isUndo) {
              e.preventDefault();
              e.stopPropagation();
              // Drop any active selection first — the restored query re-parses into new
              // tokens, so stale selected keys would highlight the wrong rows.
              if (gridSelectionRef.current.selectedKeys.size > 0) {
                gridSelectionRef.current.setSelectedKeys(new Set());
              }
              if (isRedo) {
                redo();
              } else {
                undo();
              }
              return;
            }
            // Cmd/Ctrl+A: select every token in the grid when focus is on the wrapper itself
            // or on a button descendant (e.g. a LozengeFilter cell button). FreeTextToken's
            // input handler runs first and preventDefaults; defaultPrevented short-circuits us.
            if (e.defaultPrevented) {
              return;
            }
            if (!readonly && (e.ctrlKey || e.metaKey) && e.key === 'a') {
              // Skip when the event target is an <input>: that means the user is editing
              // a cell's text and Cmd+A should select-all within the input natively, not
              // promote to a whole-query selection.
              if ((e.target as HTMLElement).tagName === 'INPUT') {
                return;
              }
              const ctx = selCtxRef.current;
              if (!ctx) {
                return;
              }
              const hasStructuredTokens = ctx.allKeys.some((k) => !ctx.isFreeTextKey(k));
              if (hasStructuredTokens) {
                e.preventDefault();
                ctx.state.selectAll(ctx.allKeys);
              }
            }
          }}
          ref={outerRef}
          className={clsx(styles.filterBarOuter, { [styles.readonly]: readonly }, className)}
          data-editable={readonly ? undefined : true}
          data-invalid={invalid || undefined}
          onBlur={(e) => {
            // Commit the query when focus leaves the filter bar entirely.
            if (!outerRef.current?.contains(e.relatedTarget as Node)) {
              dispatch({ type: 'COMMIT_QUERY' });
            }
          }}
        >
          {pinnedOptions && pinnedOptions.length > 0 && (
            <Select
              className={styles.pinnedSelect}
              aria-label={pinnedAriaLabel ?? pinnedPlaceholder}
              placeholder={pinnedPlaceholder}
              selectedKey={pinnedValue ?? null}
              isDisabled={readonly}
              onSelectionChange={(key) => {
                if (key === null || key === CLEAR_PINNED_KEY) {
                  onPinnedChange?.(undefined);
                  return;
                }
                onPinnedChange?.(String(key));
              }}
            >
              <Button variant="minimal" size="small" className={styles.pinnedTrigger}>
                <SelectValue />
                <Icon name="chevron-down" size="small" aria-hidden />
              </Button>
              <Popover placement="bottom start" shouldFlip={false}>
                <ListBox>
                  {!readonly && pinnedValue !== undefined && pinnedValue !== '' && (
                    <ListBoxItem id={CLEAR_PINNED_KEY} textValue={pinnedPlaceholder}>
                      {pinnedPlaceholder}
                    </ListBoxItem>
                  )}
                  {pinnedOptions.map((option) => (
                    <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
                      {option.label}
                    </ListBoxItem>
                  ))}
                </ListBox>
              </Popover>
            </Select>
          )}
          {showSearchIcon && !readonly && (
            <span className={styles.searchIconWrapper}>
              <Icon name="search" size="small" className={styles.searchIcon} aria-hidden />
            </span>
          )}
          {/* SelectionKeyHandler renders a visually-hidden <input>. It must NOT live inside
              the role="grid" wrapper — axe flags non-row children of role="grid". */}
          <SelectionKeyHandler inputRef={hiddenInputRef} gridRef={gridRef} />
          <div ref={gridRef} {...gridProps} data-filter-bar-row className={styles.filterBar}>
            {renderedTokens}
          </div>
          {!readonly && query.trim() && (
            <IconButton
              icon="cancel-circle-outline"
              variant="minimal"
              size="small"
              aria-label={msg('queryBuilder.filterBar.clearLabel')}
              className={styles.clearButton}
              onPress={() => {
                dispatch({ type: 'UPDATE_QUERY', query: '' });
              }}
            />
          )}
        </div>
      </KeyboardSelectionContext.Provider>
    </FilterBarContext.Provider>
  );
}

export function FilterBar(props: FilterBarProps) {
  if (props.variant === 'inline') {
    return (
      <InlineFilterMenu
        fieldOptions={props.fieldOptions}
        omitFields={props.omitFields}
        pinnedFields={props.pinnedFields}
        menuMultiSelect={props.menuMultiSelect}
        clearAllLabel={props.clearAllLabel}
        menuDisabled={props.menuDisabled}
        truncateSingleValue={props.truncateInlineSingleValue}
        className={props.className}
        readonly={props.readonly}
      />
    );
  }
  return <DefaultFilterBar {...props} />;
}
